import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit, Trash2, Shield, Crown } from "lucide-react";
import api, { handleApiError } from "@/lib/api";
import { toast } from "sonner";
import MfaBlockedBanner from "@/components/MfaBlockedBanner";

const PERM_MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "usuarios", label: "Usuários" },
  { key: "aprovacoes", label: "Aprovações" },
  { key: "solicitacoes", label: "Solicitações" },
  { key: "credores", label: "Credores" },
  { key: "empresas", label: "Empresas" },
  { key: "antecipacoes", label: "Antecipações" },
  { key: "conciliacao", label: "Conciliação" },
  { key: "auditoria", label: "Auditoria" },
  { key: "monitoramento", label: "Saúde do Sistema" },
];

const PERM_COLORS = { full: "bg-emerald-500/15 text-emerald-400", view: "bg-yellow-500/15 text-yellow-400" };

const emptyForm = { name: "", email: "", password: "", permissions: {}, is_master: false };

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [emailCheck, setEmailCheck] = useState({ exists: false, name: "", checked: false });
  const [saveError, setSaveError] = useState("");

  const isMaster = user?.is_master;
  const canManage = isMaster || ((user?.permissions || {}).usuarios === "full");
  // 2FA vencido: o backend recusa criar/editar/remover admin com 403 (FS-056).
  const mfaBlocked = !!user?.mfa_blocked;

  const fetchUsers = useCallback(async () => {
    try { const res = await api.get("/admin/admin-users"); setUsers(res.data); } catch { toast.error("Erro ao carregar usuários"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    const defaultPerms = {};
    PERM_MODULES.forEach(m => { defaultPerms[m.key] = "full"; });
    setForm({ ...emptyForm, permissions: defaultPerms, is_master: false });
    setEditingId(null);
    setEmailCheck({ exists: false, name: "", checked: false });
    setSaveError("");
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: "", permissions: u.permissions || {}, is_master: u.is_master || false });
    setEditingId(u.account_id);
    setEmailCheck({ exists: false, name: "", checked: false });
    setSaveError("");
    setDialogOpen(true);
  };

  const checkEmail = async (email) => {
    if (!email || email.length < 5 || editingId) { setEmailCheck({ exists: false, name: "", checked: false }); return; }
    try {
      const res = await api.get(`/admin/admin-users/check-email?email=${encodeURIComponent(email)}`);
      setEmailCheck({ ...res.data, checked: true });
      if (res.data.exists && res.data.name) setForm(prev => ({ ...prev, name: res.data.name }));
    } catch { setEmailCheck({ exists: false, name: "", checked: false }); }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nome e email são obrigatórios"); return; }
    if (!editingId && !emailCheck.exists && (!form.password || form.password.length < 8)) { toast.error("Senha mínima de 8 caracteres"); return; }
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        name: form.name, email: form.email, is_master: form.is_master,
        permissions: form.is_master ? null : form.permissions,
        ...(form.password ? { password: form.password } : {}),
      };
      if (editingId) {
        await api.put(`/admin/admin-users/${editingId}`, payload);
        toast.success(`${form.name} atualizado com sucesso`);
      } else {
        const res = await api.post("/admin/admin-users", payload);
        if (res.data.linked_existing) {
          toast.success(`${res.data.name} já possuía conta na Kredz e foi vinculado como admin`);
        } else {
          toast.success(`${form.name} criado como admin`);
        }
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      const msg = handleApiError(err, "Erro ao salvar");
      setSaveError(msg);
      toast.error(msg);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/admin-users/${deleteDialog}`);
      toast.success("Acesso admin removido com sucesso");
      setDeleteDialog(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover"); }
  };

  const updatePerm = (key, value) => {
    setForm(prev => ({ ...prev, permissions: { ...prev.permissions, [key]: value } }));
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="loading-spinner w-8 h-8" /></div>;

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-6 lg:space-y-8" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold tracking-tight">Usuários Admin</h1>
          <p className="text-sm text-muted-foreground mt-2">Gerencie os administradores da plataforma Kredz</p>
        </div>
        {isMaster && (
          <Button data-testid="add-admin-user-btn" onClick={openCreate} disabled={mfaBlocked}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Novo Admin
          </Button>
        )}
      </div>

      {mfaBlocked && <MfaBlockedBanner action="gerenciar usuários admin" />}

      <Card className="border-white/5">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Nome</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Tipo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Permissões</TableHead>
                {canManage && <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow><TableCell colSpan={canManage ? 5 : 4} className="text-center py-8 text-muted-foreground">Nenhum usuário admin</TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u.account_id} data-testid={`admin-user-row-${u.account_id}`} className="border-white/5 hover:bg-white/[0.02]">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{u.name}</span>
                      {u.is_master && <Crown className="w-3.5 h-3.5 text-yellow-400" />}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${u.is_master ? "bg-yellow-500/15 text-yellow-400" : "bg-blue-500/15 text-blue-400"}`}>
                      {u.is_master ? "Master" : "Normal"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {u.is_master ? (
                      <span className="text-xs text-muted-foreground">Acesso total</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {PERM_MODULES.map(m => {
                          const level = (u.permissions || {})[m.key] || "none";
                          if (level === "none") return null;
                          return <span key={m.key} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${PERM_COLORS[level]}`}>{m.label.slice(0, 5)}{level === "view" ? " (V)" : ""}</span>;
                        })}
                      </div>
                    )}
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {(!u.is_master || isMaster) && (
                          <Button variant="ghost" size="icon" data-testid={`edit-admin-user-${u.account_id}`}
                            onClick={() => openEdit(u)} disabled={mfaBlocked} className="h-8 w-8 text-muted-foreground hover:text-foreground">
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {u.id !== user?.id && (!u.is_master || isMaster) && (
                          <Button variant="ghost" size="icon" data-testid={`delete-admin-user-${u.account_id}`}
                            onClick={() => setDeleteDialog(u.account_id)} disabled={mfaBlocked}
                            className="h-8 w-8 text-muted-foreground hover:text-red-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg border-white/10 max-h-[85vh] overflow-y-auto" data-testid="admin-user-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar" : "Novo"} Administrador</DialogTitle>
            <DialogDescription>Configure o acesso do administrador à plataforma Kredz</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Nome completo <span className="text-red-400">*</span></Label>
                <Input data-testid="admin-user-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/20 border-white/10" />
              </div>
              <div className="space-y-2">
                <Label>Email <span className="text-red-400">*</span></Label>
                <Input data-testid="admin-user-email" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  onBlur={(e) => !editingId && checkEmail(e.target.value)}
                  disabled={!!editingId}
                  className="bg-black/20 border-white/10" />
              </div>
              {!editingId && emailCheck.checked && emailCheck.exists ? (
                <div className="flex items-center p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-xs text-blue-400">Este email já possui conta na Kredz (<strong>{emailCheck.name}</strong>). Será vinculado como admin com as permissões definidas abaixo.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{editingId ? "Nova senha (opcional)" : "Senha"} {!editingId && <span className="text-red-400">*</span>}</Label>
                  <Input data-testid="admin-user-password" type="password" value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={editingId ? "Manter atual" : "Mínimo 8 caracteres"} className="bg-black/20 border-white/10" />
                </div>
              )}
            </div>

            {isMaster && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-4 h-4 text-yellow-400" />
                <Label className="text-sm font-semibold">Tipo de Usuário</Label>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, is_master: true })}
                  className={`flex-1 p-3 rounded-lg border text-sm font-medium text-center transition-colors ${form.is_master ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-400" : "border-white/10 bg-black/10 text-muted-foreground hover:bg-black/20"}`}
                  data-testid="admin-type-master">
                  Master
                </button>
                <button type="button" onClick={() => setForm({ ...form, is_master: false })}
                  className={`flex-1 p-3 rounded-lg border text-sm font-medium text-center transition-colors ${!form.is_master ? "border-blue-500/40 bg-blue-500/10 text-blue-400" : "border-white/10 bg-black/10 text-muted-foreground hover:bg-black/20"}`}
                  data-testid="admin-type-normal">
                  Normal
                </button>
              </div>
              {form.is_master && (
                <p className="text-xs text-muted-foreground mt-2">Acesso total à plataforma. Pode gerenciar todos os outros administradores.</p>
              )}
            </div>
            )}

            {!form.is_master && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-blue-400" />
                  <Label className="text-sm font-semibold">Permissões</Label>
                </div>
                <div className="space-y-2">
                  {PERM_MODULES.map(m => (
                    <div key={m.key} className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5"
                      data-testid={`admin-perm-row-${m.key}`}>
                      <span className="text-sm font-medium">{m.label}</span>
                      <Select value={(form.permissions || {})[m.key] || "none"} onValueChange={(v) => updatePerm(m.key, v)}>
                        <SelectTrigger className="w-[160px] h-8 bg-black/20 border-white/10 text-xs" data-testid={`admin-perm-select-${m.key}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">Acesso Full</SelectItem>
                          <SelectItem value="view">Visualização</SelectItem>
                          <SelectItem value="none">Nenhum</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {saveError && (
            <p role="alert" data-testid="admin-user-save-error"
              className="text-sm text-red-400 p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
              {saveError}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">Cancelar</Button>
            <Button data-testid="save-admin-user-btn" onClick={handleSave} disabled={saving || mfaBlocked}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              {saving ? <div className="loading-spinner" /> : editingId ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-sm border-white/10" data-testid="delete-admin-user-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
            <DialogDescription>O administrador perderá acesso à plataforma imediatamente.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="border-white/10">Cancelar</Button>
            <Button data-testid="confirm-delete-admin-user" onClick={handleDelete} disabled={mfaBlocked}
              className="bg-red-600 hover:bg-red-700 text-white">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
